import { useEffect, useState } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents, AttributionControl } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { Map as MapIcon, Satellite } from "lucide-react";
import { getPropertyType } from "@/lib/propertyTypes";
import { summaryLine } from "@/components/ExpertiseDetailSheet";
import { unitPrice, formatUnitPrice, formatDate } from "@/lib/detailSchemas";

// Fix Leaflet default icon path issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Property-type initials for pin badges. Kept short so they fit inside the marker.
const TYPE_INITIALS = {
  arsa: "A",
  tarla: "T",
  bahce: "B",
  mesken: "M",
  ofis: "O",
  is_yeri: "İY",
  arsa_bina: "A+B",
  fabrika: "F",
};

// Distinctive rectangular badge — clearly different from Google Maps' teardrop.
// iconAnchor is at the bottom-center tail tip so the pin points EXACTLY where clicked.
const createPinIcon = (color, typeValue = "", active = false) => {
  const W = active ? 44 : 38;
  const H = active ? 30 : 26;
  const TAIL = 8;
  const TOTAL_H = H + TAIL;
  const label = TYPE_INITIALS[typeValue] || "•";
  const fontSize = label.length > 1 ? (active ? 12 : 10) : (active ? 16 : 14);
  return L.divIcon({
    className: "parsel-pin",
    html: `
      <div style="
        width:${W}px;height:${TOTAL_H}px;
        position:relative;
        filter: drop-shadow(0 2px 3px rgba(0,0,0,0.35));
      ">
        <div style="
          width:${W}px;height:${H}px;
          background:${color};
          border:2px solid #ffffff;
          border-radius:5px;
          display:flex;align-items:center;justify-content:center;
          color:#fff;font-weight:800;font-size:${fontSize}px;
          letter-spacing:0.5px;
          font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;
          line-height:1;
        ">${label}</div>
        <div style="
          position:absolute;
          left:50%;bottom:-${TAIL - 2}px;
          transform:translateX(-50%);
          width:0;height:0;
          border-left:${TAIL}px solid transparent;
          border-right:${TAIL}px solid transparent;
          border-top:${TAIL}px solid ${color};
          filter: drop-shadow(0 1px 0 #fff);
        "></div>
      </div>
    `,
    iconSize: [W, TOTAL_H],
    iconAnchor: [W / 2, TOTAL_H],
    popupAnchor: [0, -TOTAL_H + 4],
  });
};

function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom || map.getZoom(), { duration: 0.8 });
    }
  }, [center, zoom, map]);
  return null;
}

// Automatically fits the map viewport to the bounds of the given items.
// - `items`: array of records with latitude/longitude
// - `signal`: any value; whenever it changes AND items has ≥1 record, we refit.
//   This lets the parent trigger a refit on: initial load, search change, etc.
function FitBounds({ items, signal }) {
  const map = useMap();
  useEffect(() => {
    const pts = (items || [])
      .filter((it) => Number.isFinite(it.latitude) && Number.isFinite(it.longitude))
      .map((it) => [it.latitude, it.longitude]);
    if (pts.length === 0) return;
    if (pts.length === 1) {
      map.flyTo(pts[0], Math.max(map.getZoom(), 15), { duration: 0.7 });
      return;
    }
    const bounds = L.latLngBounds(pts);
    map.flyToBounds(bounds, { padding: [60, 60], duration: 0.7, maxZoom: 16 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signal]);
  return null;
}

function ClickCapture({ onPick, onContext }) {
  useMapEvents({
    click(e) {
      if (onPick) onPick(e.latlng);
    },
    contextmenu(e) {
      // Prevent browser default menu; show our own via callback.
      if (onContext) {
        e.originalEvent.preventDefault();
        onContext({ latlng: e.latlng, x: e.originalEvent.clientX, y: e.originalEvent.clientY });
      }
    },
  });
  return null;
}

// Small floating menu that appears on map right-click.
function ContextMenu({ menu, onAdd, onClose }) {
  useEffect(() => {
    if (!menu) return;
    const handler = () => onClose();
    // Close on any click OR scroll outside the menu
    window.addEventListener("click", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("click", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [menu, onClose]);
  if (!menu) return null;
  return (
    <div
      className="fixed z-[2000] bg-white border border-zinc-200 rounded-sm shadow-lg py-1 min-w-[180px] text-sm"
      style={{ left: menu.x, top: menu.y }}
      onClick={(e) => e.stopPropagation()}
      data-testid="map-context-menu"
    >
      <button
        type="button"
        onClick={() => { onAdd(menu.latlng); onClose(); }}
        className="w-full text-left px-3 py-2 hover:bg-zinc-100 flex items-center gap-2 text-zinc-800 font-medium"
        data-testid="ctx-add-expertise-btn"
      >
        <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
        Ekspertiz Ekle
      </button>
      <div className="border-t border-zinc-100 my-1" />
      <div className="px-3 py-1 text-[10px] text-zinc-400 font-mono">
        {menu.latlng.lat.toFixed(5)}, {menu.latlng.lng.toFixed(5)}
      </div>
    </div>
  );
}

export default function ExpertiseMap({
  items = [],
  activeId,
  focus,
  pickMode = false,
  pickedLatLng,
  onPick,
  onSelect,
  onAddAt,
  fitItems,
  fitSignal,
}) {
  const [layer, setLayer] = useState("satellite"); // "streets" | "satellite" — uydu varsayılan
  const [ctxMenu, setCtxMenu] = useState(null);
  return (
    <div className="h-full w-full relative" data-testid="expertise-map">
      {/* Layer toggle */}
      <div className="absolute top-3 right-3 z-[1000] bg-white border border-zinc-200 rounded-sm shadow-sm overflow-hidden flex text-xs">
        <button
          type="button"
          onClick={() => setLayer("streets")}
          className={`px-3 py-1.5 flex items-center gap-1.5 transition-colors ${
            layer === "streets" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"
          }`}
          data-testid="layer-streets-btn"
        >
          <MapIcon className="h-3.5 w-3.5" />
          Harita
        </button>
        <button
          type="button"
          onClick={() => setLayer("satellite")}
          className={`px-3 py-1.5 flex items-center gap-1.5 transition-colors ${
            layer === "satellite" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"
          }`}
          data-testid="layer-satellite-btn"
        >
          <Satellite className="h-3.5 w-3.5" />
          Uydu
        </button>
      </div>
      <MapContainer
        center={[39.9334, 32.8597]}
        zoom={6}
        className="h-full w-full"
        scrollWheelZoom
        attributionControl={false}
      >
        <AttributionControl
          position="bottomright"
          prefix='<a href="https://leafletjs.com" target="_blank" rel="noopener noreferrer">Leaflet</a>'
        />
        {layer === "streets" ? (
          <TileLayer
            key="osm"
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        ) : (
          // Google Hybrid: satellite imagery WITH place labels & roads baked in.
          // `lyrs=y` returns the hybrid layer; subdomains mt0-mt3 for CDN spread.
          <TileLayer
            key="google-hybrid"
            attribution='&copy; <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer">Google</a>'
            url="https://mt{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
            subdomains={["0", "1", "2", "3"]}
            maxZoom={20}
          />
        )}
      {focus && <FlyTo center={focus.center} zoom={focus.zoom} />}
      <FitBounds items={fitItems} signal={fitSignal} />
      {pickMode ? (
        <ClickCapture onPick={onPick} />
      ) : (
        <ClickCapture onContext={(m) => setCtxMenu(m)} />
      )}

      <MarkerClusterGroup
        chunkedLoading
        showCoverageOnHover={false}
        maxClusterRadius={50}
        disableClusteringAtZoom={16}
        spiderfyOnMaxZoom
      >
      {items.map((it) => {
        const type = getPropertyType(it.property_type);
        const up = unitPrice(it);
        return (
          <Marker
            key={it.id}
            position={[it.latitude, it.longitude]}
            icon={createPinIcon(type.color, it.property_type, it.id === activeId)}
          >
            <Popup>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect && onSelect(it);
                }}
                className="text-left cursor-pointer w-full hover:bg-zinc-50 -m-2 p-2 rounded transition-colors"
                data-testid={`popup-open-${it.id}`}
                title="Detay panelini aç"
              >
                <div className="text-sm" style={{ minWidth: 200 }}>
                  <div className="flex items-center gap-2">
                    <span
                      style={{
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: type.color,
                      }}
                    />
                    <span className="font-semibold text-zinc-900">{type.label}</span>
                  </div>
                  <div className="mt-1.5 font-medium text-zinc-800">
                    {it.neighborhood_name} Mah.
                  </div>
                  <div className="text-zinc-600 text-xs">
                    {it.district_name} / {it.province_name}
                  </div>
                  <div className="mt-1 text-zinc-500 text-xs font-mono">
                    Ada {it.ada} · Parsel {it.parsel}
                  </div>
                  {it.expertise_date && (
                    <div className="mt-0.5 text-zinc-500 text-[11px] font-mono">
                      Ekspertiz: {formatDate(it.expertise_date)}
                    </div>
                  )}
                  {up && (
                    <div
                      className="mt-1.5 inline-block bg-amber-100 border border-amber-300 text-amber-900 text-xs font-mono font-bold rounded-sm px-1.5 py-0.5"
                    >
                      {formatUnitPrice(up)}
                    </div>
                  )}
                  {summaryLine(it) && (
                    <div className="mt-1.5 text-xs text-zinc-700 font-mono">
                      {summaryLine(it)}
                    </div>
                  )}
                  {it.notes && (
                    <div className="mt-2 pt-2 border-t border-zinc-100 text-xs text-zinc-600 line-clamp-3">
                      {it.notes}
                    </div>
                  )}
                  <div className="mt-2 pt-2 border-t border-zinc-100 text-[11px] text-blue-600 font-semibold">
                    Detayları göster →
                  </div>
                </div>
              </button>
            </Popup>
          </Marker>
        );
      })}
      </MarkerClusterGroup>

      {pickMode && pickedLatLng && (
        <Marker
          position={[pickedLatLng.lat, pickedLatLng.lng]}
          icon={createPinIcon("#2563EB", "", true)}
        />
      )}
    </MapContainer>
    <ContextMenu
      menu={ctxMenu}
      onAdd={(latlng) => onAddAt && onAddAt(latlng)}
      onClose={() => setCtxMenu(null)}
    />
    </div>
  );
}
